import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, inject, OnDestroy, OnInit, Output, Renderer2, signal, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { DialogModule } from 'primeng/dialog';
import { takeUntil } from 'rxjs';
import { TreeSelectModule } from 'primeng/treeselect';
import { TreeNode } from 'primeng/api';
import { ChipModule } from 'primeng/chip';

import { Destroyable } from '@src/app/utils/Destroyable';
import { FascadeTachographDriverFileStatusService } from './fascade-tachograph-driver-file-status.service';
import { TachographFilesListComponent } from '../components/tachograph-files-list/tachograph-files-list.component';
import { IGetDriverTachographFileSchedule } from '@src/app/interfaces/api/files-ddd.interface';
import { InputComponent } from '@src/app/standalone-components/input/input.component';
import { WorkerService } from '@layout/driver/services/worker.service';
import { Driver } from '@commons/models/domain/Driver';
import { UserActivityAutoRefreshManager } from '../user-activity-auto-refresh.manager';
import { FilesDddService } from '@src/app/services/api/files-ddd.service';

const imports = [
  TranslateModule,
  CommonModule,
  InputComponent,
  ButtonModule,
  TableModule,
  CheckboxModule,
  ReactiveFormsModule,
  SkeletonModule,
  DialogModule,
  TachographFilesListComponent,
  DialogModule,
  TreeSelectModule,
  FormsModule,
  ChipModule
];

@Component({
  selector: 'app-tachograph-drivers-files-status',
  standalone: true,
  imports,
  templateUrl: './tachograph-drivers-files-status.component.html',
  styleUrls: ['./tachograph-drivers-files-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TachographDriversFilesStatusComponent extends Destroyable implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('containerList') private containerListRef: ElementRef<HTMLDivElement>;
  @Output() public openFiles$: EventEmitter<string> = new EventEmitter();

  public readonly filesDriverFasade = inject(FascadeTachographDriverFileStatusService);
  private readonly renderer2 = inject(Renderer2);
  private readonly workerService = inject(WorkerService);
  private readonly filesDDDService = inject(FilesDddService);

  public searchValueControl: FormControl = new FormControl('');
  public selectedItems = signal<IGetDriverTachographFileSchedule[]>([]);
  public isLoadingInit = signal(true);

  public isCheckedAll: boolean = false;
  public listHeight: string;

  public visibleForm: boolean;
  public driverTreeList = signal<TreeNode<Driver>[]>([]);
  public selectedDriverForm: TreeNode<Driver>[] = [];

  public isSavingForm = signal(false);
  public isDeletingItems = signal(false);

  private autoRefresherRef: UserActivityAutoRefreshManager;

  ngOnInit(): void {
    this.autoRefresherRef = new UserActivityAutoRefreshManager(() => this.autoRefreshFilesStatus());
    this.autoRefresherRef.init();
  }

  override ngOnDestroy(): void {
    this.destroy();
    this.autoRefresherRef.destroy();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.setListHeight();
    }, 0);
  }

  public onChangeDownloadMode({ value, item }: any): void {
    // this.filesDriverFasade.SetTachographScheduleForDrivers([item.DriverId], value).pipe(takeUntil(this.destroy$)).subscribe();
  }

  public deleteDrivers(): void {
    if (this.selectedItems().length) {
      this.isDeletingItems.set(true);
      const driverIds = this.selectedItems().map(item => item.DriverId);

      this.filesDriverFasade.SetTachographScheduleForDrivers(driverIds, false).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.isCheckedAll = false;
          this.onChangeValueCheckedAll(false);
          this.autoRefresherRef.refreshNow();
          this.isDeletingItems.set(false);
        }
      });
    }
  }

  public onAddDrivers(): void {
    this.isSavingForm.set(true);

    setTimeout(() => {
      const driverIds = this.selectedDriverForm.filter(item => !item.children).map(item => item.data!.Id);

      this.filesDriverFasade.SetTachographScheduleForDrivers(driverIds).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.addedNewDrivers();
        }
      });
    }, 100);
  }

  private addedNewDrivers(): void {
    this.visibleForm = false;
    this.isSavingForm.set(false);
    this.selectedDriverForm = [];
    this.autoRefresherRef.refreshNow();
  }

  public showModalAddDriver(): void {
    if (!this.driverTreeList().length) {
      this.setDriverTreeList();
    }
    this.visibleForm = true;
  }

  private setDriverTreeList(): void {
    this.filesDDDService.GetDriversWithTachographDisabled().subscribe({
      next: (data) => {
        const driversGroup = this.workerService.makeGroupedWorkers(data);
        const workers: TreeNode<Driver>[] = [];

        driversGroup.groups.forEach((value, key) => {
          workers.push({
            label: value,
            key: key.toString(),
            children: driversGroup.list.get(key),
            expanded: true,
          })
        });
        this.driverTreeList.set(workers);
      }
    });
  }

  private autoRefreshFilesStatus(): void {
    this.filesDriverFasade.GetDriverTachographFileSchedule(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoadingInit.set(false);
        }
      });
  }

  public forceFile(data: { button: HTMLButtonElement, item: IGetDriverTachographFileSchedule }): void {
    this.renderer2.setProperty(data.button, 'disabled', true);

    this.filesDriverFasade.ForceDriverFile(data.item.DriverId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        // this.rendere2.setProperty(button, 'disabled', false);
      }
    });
  }

  public onChangeValueCheckedAll(value: boolean): void {
    this.selectedItems.set(value ? [...this.filesDriverFasade.driverList()] : []);
  }

  public onCheckedItem(list: IGetDriverTachographFileSchedule[]): void {
    this.selectedItems.set(list);
  }

  private setListHeight(): void {
    this.listHeight = (this.containerListRef.nativeElement.clientHeight - 45) + 'px';
    this.renderer2.setStyle(document.getElementsByClassName('p-datatable-table-container').item(0), 'max-height', this.listHeight);
  }

}
